import { NgModule, ApplicationRef, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { environment } from '../environments/environment';
import { HttpClientModule, HttpClient, HttpResponse, HTTP_INTERCEPTORS, HttpHandler } from '@angular/common/http';
import { App } from './app.component';
import { AppState } from './app.service';

import { JobsPollingService } from './services/job-poller.service';
import { JobsNotifierService } from './services/job-notifier.service';
import { ToastNotifierService } from './services/toast-notifier.service';
import { RefreshSharedService } from './refresh-shared-service';

import { AlarmsPollingService } from './services/alarm-poller.service';
import { AlarmsNotifierService } from './services/alarm-notifier.service';

import { VcaCommonModule } from '../common';
import { ToastModule } from '../common/toasts';

import Dashboard from './components/dashboard/dashboard.component';
import NotFound from '../common/not-found/not-found.component';
import ServiceUnavailable from './components/service-unavailable/service-unavailable.component';
import ServiceUnAuthorized from './components/service-unauthorized/service-unauthorized.component';
import { PublisherAppModule } from '../publisher/src/publisher-ui/publisher.module';
import { TroubleShootingAppModule } from '../troubleshooting/src/troubleshooting-ui/troubleshooting.module';
import { AlertsAppModule } from '../alerts/src/alerts-ui/alerts.module';

import MiniActivityLog from './components/activity-log/mini-activity-log.component';
import MainActivityLog from './components/activity-log/main-activity-log.component';
import AboutComponent from './components/about/about.component';

import DashboardMiniActivityLog from './components/activity-log/dashboard-mini-activity-log.component';
import { ServiceHeaderDirective } from './components/service-header/service-header.directive';

import { TranslateModule } from '@ngx-translate/core';
import {
    TranslateService,
    TranslateLoader
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslationMock } from './translation.mock';
import { ContextMenuService } from '../common/context-menu/context-menu.service';

import { ClarityModule } from '@clr/angular';
import { LoginComponent } from './auth/login.component';
import { LogoutComponent } from './auth/logout.component';
import { AuthService } from './auth/auth.service';
import { AuthGuard } from './auth/auth.guard';

import { InventoryModule } from './components/inventory-main';
import { ServicesModule } from './components/services-main/services.module';
import { AdministrationModule } from './components/administration-main/administration.module';

import { LoginRoutingModule } from './auth/login-routing.module';
import { AppRoutingModule } from './app-routing.module';

import { ActivityLogModule } from './components/activity-log/activity-log.module';
import { DirectConnectModule } from './components/network/direct-connect/direct-connect.module';
import { Ng2Webstorage } from 'ngx-webstorage';
import { ResizableModule } from 'angular-resizable-element';
import { GlobalRef, BrowserGlobalRef } from '../common/global-variables';
import { SharedModule } from '../common/shared.module';

import { LicenseAlertService } from './services/license-alert.service';
import { NspUpgradeService } from './../publisher/src/publisher-ui/upgrade/nsp-upgrade.service';
import { GuidedTourBuilder } from './services/guided-tour-builder/guided-tour-builder.service';
import { AuditLogAppModule } from '../auditlog/src/auditlog-ui/auditlog.module';

import '../styles/styles.scss';
import { AngularFontAwesomeModule } from 'angular-font-awesome';

import { CustomRequestOptions } from '../common/http/custom-request-options';
import { CustomHttp } from '../common/http/custom-http';
import { RouterModule, PreloadAllModules, Router } from '@angular/router';
import { VcaAlertsService } from '../common/vca-alerts';
import { FleetModule } from './components/fleet/fleet.module';
import { HybridInterconnectModule } from './components/hybrid-interconnect/hybrid-interconnect.module';
import { SitePairingModule } from './components/sitepairing/site-pairing.module';
import { UnderlayModule } from './components/underlay-app/underlay-app.module';
import { FeatureEnablementService } from './services/feature-enablement.service';
import { HybridInterconnectService } from './components/hybrid-interconnect/hybrid-interconnect.service';
import { FleetQueryService } from './services/fleet-query.service';

import { DatePipe } from '@angular/common';
import { AuthTokenInterceptor } from './interceptors/auth-token-interceptor';
import { RequestOptions } from '@angular/http';

function waitForRemotePluginSdkLoad(): Promise<any> {
    return new Promise((resolve, reject) => {
        if(location.href.indexOf(`/plugins/com.vmware.hcx.plugin`) > -1) {
            const sdkScript = document.createElement('script');
            sdkScript.id = 'plugin-sdk-js';
            sdkScript.type = 'text/javascript';
            sdkScript.src = '/api/ui/htmlClientSdk.js';
            document.head.appendChild(sdkScript);
            if(sdkScript){
                sdkScript.addEventListener('load', () => {
                    // update sdk object ref in env.
                    environment.htmlClientSdk = (<any>window).htmlClientSdk;
                    console.log('remote plugin js sdk load success');
                    resolve(true);
                });
                sdkScript.addEventListener('error', () => {
                    console.log('remote plugin js sdk load error');
                    resolve(true);
                });
            }
        } else {
            console.log('remote plugin sdk script not requested');
            resolve(true);
        }
    });
}
const APP_PROVIDERS = [
    DatePipe,
    TranslationMock,
    TranslateService,
    AppState,
    AuthService,
    AuthGuard,
    ContextMenuService,
    JobsPollingService,
    JobsNotifierService,
    ToastNotifierService,
    RefreshSharedService,
    AlarmsPollingService,
    AlarmsNotifierService,
    LicenseAlertService,
    NspUpgradeService,
    GuidedTourBuilder,
    {
        provide: RequestOptions,
        useClass: CustomRequestOptions
    },
    {
        provide: HttpClient,
        useFactory: (
            backend: HttpHandler,
            defaultOptions: RequestOptions,
            router: Router,
            alert: VcaAlertsService
        ) => new CustomHttp(backend, defaultOptions, router, alert),
        deps: [HttpHandler, RequestOptions, Router, VcaAlertsService]
    },
    FeatureEnablementService,
    HybridInterconnectService,
    FleetQueryService
];

@NgModule({
    bootstrap: [App],
    declarations: [
        App,
        ServiceHeaderDirective,
        Dashboard,
        MiniActivityLog,
        MainActivityLog,
        DashboardMiniActivityLog,
        NotFound,
        ServiceUnavailable,
        ServiceUnAuthorized,
        LoginComponent,
        LogoutComponent,
        AboutComponent
    ],
    imports: [
        FleetModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HttpClientModule,
        ClarityModule.forRoot(),
        VcaCommonModule,
        InventoryModule,
        ServicesModule,
        AdministrationModule,
        PublisherAppModule,
        TroubleShootingAppModule,
        AlertsAppModule,
        AuditLogAppModule,
        LoginRoutingModule,
        AppRoutingModule,
        ActivityLogModule,
        DirectConnectModule,
        AngularFontAwesomeModule,
        Ng2Webstorage,
        ResizableModule,
        HybridInterconnectModule,
        SitePairingModule,
        UnderlayModule,

        ToastModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (http: HttpClient) =>
                    new TranslateHttpLoader(http, 'assets/i18n/app/', '.json'),
                deps: [HttpClient]
            }
        }),
        SharedModule.forBrowser()
    ],
    providers: [
        environment.ENV_PROVIDERS,
        APP_PROVIDERS,
        { provide: GlobalRef, useClass: BrowserGlobalRef },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthTokenInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory:() => waitForRemotePluginSdkLoad,
            multi: true
        }
    ]
})
export class AppModule {
    constructor(public appRef: ApplicationRef, public appState: AppState) { }
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts